import React from 'react';
import { createCodebookCache } from '../utils/codebookCache';

export interface ProductionStatusOption {
  _id: string;
  name: string;
  description?: string;
  code?: string;
  order?: number;
}

// Size variants, same convention as MachineSelect / SpreadsheetPartnerInput.
// `table` exists so spreadsheet cells never bend this component from the call
// site with `!important` utility overrides (CLAUDE.md rule 7b). Focus state is
// the canonical blue accent, matching the sibling cells in those tables.
const SIZE_STYLES = {
  default: 'h-10 px-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200',
  table: 'h-6 px-1 border-0 rounded-none outline-none hover:bg-blue-50 focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50',
} as const;

export type ProductionStatusSelectSize = keyof typeof SIZE_STYLES;

// ─── Production status cache (via shared codebook-cache factory) ─────────────
// SWR + subscriber broadcast + auto-invalidate on writes to /api/production-statuses
// all come from the factory — see utils/codebookCache.ts.

const productionStatusCache = createCodebookCache<ProductionStatusOption>({
  endpoint: '/api/production-statuses',
  params: { limit: 200, sort: 'order', order: 'asc' },
  select: (data) => (Array.isArray(data) ? data : data?.rows || data?.data || data?.items || []),
});

/** Call after any successful create/update/delete so next mount fetches fresh data. */
export const invalidateProductionStatusCache = productionStatusCache.invalidate;
/** Force a fetch now and broadcast to all mounted selects. */
export const refetchProductionStatusOptions = productionStatusCache.refetch;
/** Hook: production status options (stale-while-revalidate, live-updating). */
export const useProductionStatusOptions = (): ProductionStatusOption[] => productionStatusCache.useOptions();

interface ProductionStatusSelectProps {
  value: string | null | undefined;
  onChange: (id: string | null, option?: ProductionStatusOption | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  size?: ProductionStatusSelectSize;
  onKeyDown?: (e: React.KeyboardEvent<HTMLSelectElement>) => void;
  onBlur?: () => void;
  allowedCodes?: string[];
}

const ProductionStatusSelect = React.forwardRef<HTMLSelectElement, ProductionStatusSelectProps>(({
  value,
  onChange,
  disabled = false,
  placeholder = '—',
  className = '',
  size = 'default',
  onKeyDown,
  onBlur,
  allowedCodes,
}, ref) => {
  const options = useProductionStatusOptions();
  const visibleOptions = allowedCodes
    ? options.filter((option) => allowedCodes.includes(option.code || "") || option._id === value)
    : options;
  // A borderless cell sits on the row's own background; only the standalone control paints white.
  const background = disabled
    ? 'bg-gray-50 cursor-not-allowed'
    : size === 'table'
    ? 'bg-transparent'
    : 'bg-white';

  return (
    <select
      ref={ref}
      value={value ?? ''}
      onChange={(e) => {
        const id = e.target.value || null;
        const opt = id ? options.find((o) => o._id === id) || null : null;
        onChange(id, opt);
      }}
      onMouseDown={() => refetchProductionStatusOptions()}
      onFocus={() => refetchProductionStatusOptions()}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      disabled={disabled}
      className={`${SIZE_STYLES[size]} ${background} ${className}`}
    >
      <option value="">{placeholder}</option>
      {visibleOptions.map((o) => (
        <option key={o._id} value={o._id}>
          {o.name}
        </option>
      ))}
    </select>
  );
});

export default ProductionStatusSelect;
